import { ClinicalTrialUpdateType } from '@dynamika-shared/types/clinicalTrials';
import { DatePicker } from '@dynamika-shared/ui/DatePicker/DatePicker';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { valueType } from 'antd/lib/statistic/utils';
import { CalendarClearIcon } from '@dynamika-shared/ui/icons';
import { FieldErrors } from 'react-hook-form';
import { formatFormErrors } from '@utils/formatFormErrors';
import { twMerge } from '@utils/twMerge';
import { Option } from '@utils/generateOptions';
import { format } from 'date-fns';

type RenderInputProps = {
  label: string;
  name: keyof ClinicalTrialUpdateType;
  required: boolean;
  maxLength?: number;
  errors: FieldErrors<ClinicalTrialUpdateType>;
  watch: (name: string) => valueType;
  onChangeField: (name: keyof ClinicalTrialUpdateType, value: string) => void;
  onBlur: (name: keyof ClinicalTrialUpdateType, value: string) => void;
  asText?: boolean;
  classNameLabelText?: string;
  disabled?: boolean;
};

type RenderSelectProps = {
  label: string;
  name: keyof ClinicalTrialUpdateType;
  required: boolean;
  errors: FieldErrors<ClinicalTrialUpdateType>;
  watch: (name: string) => valueType;
  onChangeField: (
    name: keyof ClinicalTrialUpdateType,
    value: string | undefined
  ) => void;
  options: Array<Option>;
  showSearch?: boolean;
  asText?: boolean;
  disabled?: boolean;
};

type RenderDatePickerProps = {
  startDate: keyof ClinicalTrialUpdateType;
  endDate: keyof ClinicalTrialUpdateType;
  watch: (name: string) => valueType;
  errors: FieldErrors<ClinicalTrialUpdateType>;
  onChangeField: (
    name: keyof ClinicalTrialUpdateType,
    value: string | undefined
  ) => void;
  disabled?: boolean;
};

export const renderSelect = ({
  label,
  name,
  required,
  watch,
  errors,
  onChangeField,
  options,
  showSearch = true,
  asText,
  disabled
}: RenderSelectProps) => {
  return (
    <>
      <Select
        required={required}
        showSearch={showSearch}
        filterOption={(input, option) =>
          ((option?.label ?? '') as string)
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        listHeight={160}
        options={options}
        value={watch(name as string)}
        onChange={e => onChangeField(name, e)}
        errors={formatFormErrors(errors?.[name]?.message)}
        inOneLine={true}
        name={name}
        label={label}
        placeholder={label}
        data-testid={name as string}
        classNameLabel='pl-1.5'
        asText={asText}
        disabled={disabled}
      />
      <hr className='mb-1.5 mt-3' />
    </>
  );
};

export const renderInput = ({
  label,
  name,
  required,
  watch,
  errors,
  onChangeField,
  onBlur,
  maxLength,
  asText,
  classNameLabelText,
  disabled
}: RenderInputProps) => (
  <>
    <Input
      inOneLine={true}
      required={required}
      data-testid={name}
      placeholder={label}
      label={label}
      name={name}
      value={watch(name)}
      errors={formatFormErrors(errors?.[name]?.message)}
      onChange={e => onChangeField(name, e.target.value)}
      onBlur={e => onBlur(name, e.target.value.trim())}
      classNameLabel='pl-1.5 opacity-100'
      maxLength={maxLength}
      asText={asText}
      classNameLabelText={classNameLabelText}
      disabled={disabled}
    />
    <hr className='mb-1.5 mt-3' />
  </>
);

export const renderDatePickerStart = ({
  watch,
  onChangeField,
  startDate,
  endDate,
  errors,
  disabled
}: RenderDatePickerProps) => (
  <div className='flex flex-col justify-between'>
    <DatePicker
      value={watch(startDate) ? new Date(watch(startDate) || new Date()) : null}
      onChange={(date: Date | null) =>
        onChangeField(startDate, date ? format(date, 'yyyy-MM-dd') : undefined)
      }
      data-testid={'startDate'}
      name={startDate}
      defaultValue={new Date()}
      format={{
        format: 'DD.MM.YYYY',
        type: 'mask',
      }}
      suffixIcon={<CalendarClearIcon className='h-2 w-2' />}
      disabledDate={current => {
        current.setHours(0, 0, 0, 0);
        const minDate = new Date();
        minDate.setHours(0, 0, 0, 0);
        minDate.setFullYear(minDate.getFullYear() - 10);
        if (current && current.getTime() < minDate.getTime()) {
          return true;
        }
        const endDateValue = watch(endDate);
        if (endDateValue) {
          const endDate = new Date(endDateValue);
          return current && current.getTime() > endDate.getTime();
        }
        return false;
      }}
      className={twMerge(
        'h-6 max-h-6 w-[18rem] max-w-[18rem] grow rounded-3xl',
        errors?.[startDate] ? 'border-redOrange bg-seashell' : ''
      )}
      placeholder={'Start date'}
      disabled={disabled}
    />
    <span
      data-testid={`${startDate}-error-container`}
      className={twMerge(
        'ant-select-error text-1420 font-normal max-w-[18rem] pl-1',
        errors ? 'visible' : 'invisible'
      )}
    >
      {formatFormErrors(errors?.[startDate]?.message)}
    </span>
  </div>
);

export const renderDatePickerEnd = ({
  watch,
  onChangeField,
  startDate,
  endDate,
  errors,
  disabled
}: RenderDatePickerProps) => (
  <div className='flex flex-col justify-between'>
    <DatePicker
      value={watch(endDate) ? new Date(watch(endDate) || new Date()) : null}
      onChange={(date: Date | null) =>
        onChangeField(endDate, date ? format(date, 'yyyy-MM-dd') : undefined)
      }
      disabledDate={current => {
        current.setHours(0, 0, 0, 0);
        return (
          current &&
          current.getTime() <
            (watch(startDate)
              ? new Date(watch(startDate) || new Date())
              : new Date()
            ).getTime()
        );
      }}
      name={endDate}
      format={{
        format: 'DD.MM.YYYY',
        type: 'mask',
      }}
      suffixIcon={<CalendarClearIcon className='h-2 w-2' />}
      className={twMerge(
        'h-6 max-h-6 w-[18rem] max-w-[18rem] grow rounded-3xl',
        errors?.[endDate] ? 'border-redOrange bg-seashell' : ''
      )}
      placeholder={'End date'}
      data-testid={'endDate'}
      disabled={disabled}
    />
    <span
      data-testid={`${endDate}-error-container`}
      className={twMerge(
        'ant-select-error text-1420 font-normal max-w-[18rem] pl-1',
        errors ? 'visible' : 'invisible'
      )}
    >
      {formatFormErrors(errors?.[endDate]?.message)}
    </span>
  </div>
);
